/**
 * KAN-958: кнопка "Колонки" в панели фильтров таблицы CRM (счётчик скрытых) и
 * модалка со всеми столбцами таблицы: флажок показывает или скрывает столбец,
 * обязательный (Телефон, Контакт, Действия) отмечен и неактивен, "Показать все"
 * возвращает скрытые. Компонент только рисует; состояние даёт useCrmTableColumns.
 */
import { useState } from 'react';
import { Columns3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { CrmTableColumn } from '@/lib/crmTableColumns';
import { cn } from '@/lib/utils';

interface CrmColumnsDialogProps {
  columns: readonly CrmTableColumn[];
  hidden: ReadonlySet<string>;
  onToggle: (code: string, visible: boolean) => void;
  onShowAll: () => void;
}

export function CrmColumnsDialog({ columns, hidden, onToggle, onShowAll }: CrmColumnsDialogProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const hiddenCount = hidden.size;
  return (
    <>
      <button
        type="button"
        data-testid="crm-columns-button"
        onClick={() => setOpen(true)}
        title="Колонки таблицы"
        className={cn(
          'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-sm font-medium transition-colors',
          hiddenCount > 0 ? 'border-blue-600 bg-blue-50 text-blue-800' : 'bg-white text-gray-700 hover:bg-gray-50'
        )}
      >
        <Columns3 className="h-4 w-4" aria-hidden="true" />
        Колонки
        {hiddenCount > 0 && (
          <span
            data-testid="crm-columns-hidden-count"
            aria-label={`Скрыто колонок: ${hiddenCount}`}
            className="rounded-full bg-blue-600 px-1.5 text-xs tabular-nums text-white"
          >
            {hiddenCount}
          </span>
        )}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Колонки таблицы</DialogTitle>
            <DialogDescription>Отмеченные колонки видны в таблице. Настройка сохраняется для вас.</DialogDescription>
          </DialogHeader>
          <ul className="max-h-[60vh] space-y-1 overflow-y-auto" data-testid="crm-columns-list">
            {columns.map((column) => {
              const id = `crm-column-${column.code}`;
              return (
                <li key={column.code} className="flex items-center gap-2 py-0.5">
                  <Checkbox
                    id={id}
                    aria-label={column.label}
                    checked={column.required || !hidden.has(column.code)}
                    disabled={column.required}
                    onCheckedChange={(checked) => onToggle(column.code, checked)}
                  />
                  <label
                    htmlFor={id}
                    className={cn('text-sm', column.required ? 'text-gray-400' : 'cursor-pointer text-gray-800')}
                  >
                    {column.label}
                    {column.required && <span className="ml-1 text-xs">(всегда видна)</span>}
                  </label>
                </li>
              );
            })}
          </ul>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={hiddenCount === 0} onClick={onShowAll}>
              Показать все
            </Button>
            <Button type="button" onClick={() => setOpen(false)}>
              Готово
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
